import { LoaderWrapper } from '@src/components/Loader';
import { changeOrganization, unmorph } from '@src/utils/admin';
import {
  Button,
  FormControlSizes,
  LoaderTalos,
  logger,
  useConstant,
  useCurrenciesContext,
  useMarketAccountsContext,
  useMarketsContext,
  useOrganizationContext,
  useSecuritiesContext,
  useStrategiesContext,
  useUnifiedLiquidityContext,
  useUserContext,
  type User,
} from '@talos/kyoko';
import { useCallback, useEffect, useState, type PropsWithChildren } from 'react';

import { useDynamicDocumentTitle } from '../hooks/useDynamicDocumentTitle';
import { useDynamicFavicon } from '../hooks/useDynamicFavicon';
import { useOrgConfiguration } from './OrgConfigurationProvider';
import { useSubAccounts } from './SubAccountsContext';
import { useUsersContext } from './UsersContext';

export const ProvidersGate = ({ children }: PropsWithChildren) => {
  const { isLoaded: isMarketsReady } = useMarketsContext();
  const { isLoaded: isCurrenciesReady } = useCurrenciesContext();
  const { isLoaded: isSecuritiesReady } = useSecuritiesContext();
  const { isLoaded: isMarketAccountsReady } = useMarketAccountsContext();
  const { isLoaded: isSubAccountsReady } = useSubAccounts();
  const { isLoaded: isUsersReady } = useUsersContext();
  const { isLoaded: isStrategiesReady } = useStrategiesContext();
  const { isLoaded: isOrganizationReady } = useOrganizationContext();
  const { isLoaded: isUnifiedLiquidityReady } = useUnifiedLiquidityContext();
  const { isLoaded: isOrgConfigurationLoaded } = useOrgConfiguration();
  const [providersReady, setProvidersReady] = useState(false);
  const [disconnected, setDisconnected] = useState(false);

  const { user } = useUserContext() as { user: User };

  useDynamicDocumentTitle();
  useDynamicFavicon();

  useEffect(() => {
    setProvidersReady(
      [
        isMarketsReady,
        isCurrenciesReady,
        isSecuritiesReady,
        isMarketAccountsReady,
        isSubAccountsReady,
        isUsersReady,
        isStrategiesReady,
        isOrganizationReady,
        isUnifiedLiquidityReady,
        isOrgConfigurationLoaded,
      ].every(item => item === true)
    );
  }, [
    isMarketsReady,
    isCurrenciesReady,
    isSecuritiesReady,
    isMarketAccountsReady,
    isSubAccountsReady,
    isUsersReady,
    isStrategiesReady,
    isOrganizationReady,
    isUnifiedLiquidityReady,
    isOrgConfigurationLoaded,
  ]);

  useEffect(() => {
    const ready = providersReady;
    const timeout = setTimeout(
      () => {
        setDisconnected(!ready);
        if (!ready) {
          logger.warn('[pg] Providers not ready within timeout');
        }
      },
      2 * 60 * 1000
    );
    return () => {
      clearTimeout(timeout);
    };
  }, [providersReady]);

  const handleUnmorph = useCallback(() => {
    if (user.IsMorphed) {
      unmorph().then(() => (window.location.href = '/'));
    } else if (user.UsingOrgID) {
      changeOrganization(user.OrgID).then(() => (window.location.href = '/'));
    }
  }, [user]);

  const firstRenderTimeStamp = useConstant(Date.now());
  useEffect(() => {
    if (providersReady) {
      logger.trackDuration('ProvidersGate', {
        startTime: firstRenderTimeStamp,
        duration: Date.now() - firstRenderTimeStamp,
      });
    }
  }, [providersReady, firstRenderTimeStamp]);

  if (disconnected) {
    return (
      <LoaderWrapper>
        <p>Disconnected!</p>
        {(user?.IsMorphed || user?.UsingOrgID) && (
          <Button size={FormControlSizes.Small} onClick={handleUnmorph}>
            Unmorph
          </Button>
        )}
      </LoaderWrapper>
    );
  }

  return <>{providersReady ? children : <LoaderTalos />}</>;
};
